import {FormControl, InputLabel, Select, MenuItem, Box, Typography} from "@mui/material";
import {RTL} from "../../cache/CacheRtl";
import {colors, borders, shadows} from "./colors";

/**
 * Reusable styled filter dropdown.
 *
 * Props:
 *  - label: string — dropdown label
 *  - value: any — current selected value
 *  - onChange: (value) => void
 *  - options: Array<{ value, label, color?, divider? }>
 *      color: optional dot color for the option
 *      divider: if true, renders as a disabled section header
 *  - fullWidth?: boolean
 *  - minWidth?: number | string
 *  - size?: "small" | "medium"
 *  - sx?: object
 */
const FilterDropdown = ({
                          label,
                          value,
                          onChange,
                          options = [],
                          fullWidth = false,
                          minWidth = 140,
                          size = "small",
                          sx,
                        }) => {
  return (
      <RTL>
        <FormControl size={size} fullWidth={fullWidth} sx={{minWidth, ...sx}}>
          <InputLabel
              sx={{
                fontSize: "14px",
                color: colors.textSecondary,
                "&.Mui-focused": {color: colors.primary},
              }}
          >
            {label}
          </InputLabel>
          <Select
              value={value}
              onChange={(e) => onChange(e.target.value)}
              label={label}
              sx={{
                backgroundColor: colors.white,
                borderRadius: borders.radius.md,
                fontSize: "14px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.border,
                  borderRadius: borders.radius.md,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.primary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.primary,
                  borderWidth: "2px",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: borders.radius.md,
                    border: borders.default,
                    boxShadow: shadows.dropdown,
                    marginTop: "4px",
                  }
                }
              }}
          >
            {options.map((option, idx) => {
              if (option.divider) {
                return (
                    <MenuItem key={`divider-${idx}`} disabled sx={{paddingTop: 1.5}}>
                      <Typography sx={{fontSize: "12px", fontWeight: 600, color: colors.textMuted}}>
                        {option.label}
                      </Typography>
                    </MenuItem>
                );
              }
              return (
                  <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{
                        fontSize: "14px",
                        padding: "8px 14px",
                        margin: "2px 6px",
                        borderRadius: borders.radius.sm,
                        "&:hover": {
                          backgroundColor: colors.primarySubtle,
                          color: colors.primary,
                        },
                        "&.Mui-selected": {
                          backgroundColor: colors.primaryLight,
                          color: colors.primary,
                          fontWeight: 600,
                          "&:hover": {
                            backgroundColor: colors.primarySubtle,
                          },
                        },
                      }}
                  >
                    {option.color ? (
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                          <Box sx={{
                            width: "8px",
                            height: "8px",
                            borderRadius: borders.radius.full,
                            backgroundColor: option.color,
                            flexShrink: 0,
                          }}/>
                          <Typography sx={{fontSize: "14px"}}>{option.label}</Typography>
                        </Box>
                    ) : (
                        <Typography sx={{fontSize: "14px"}}>{option.label}</Typography>
                    )}
                  </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </RTL>
  );
};

export default FilterDropdown;
